import React, { useEffect, useRef, useState } from 'react';

/**
 * Anneau de progression circulaire SVG animé.
 * - S'anime de 0 vers `pct` au mount + à chaque update
 * - Affiche le pourcentage centré + label optionnel
 * - Couleur primaire brand par défaut, mais accepte une override
 * - Respecte prefers-reduced-motion (pas d'easing)
 */
export function ScoreRing({
  value = 0,            // numérateur
  total = 100,          // dénominateur
  size = 76,
  stroke = 8,
  color,                // CSS color (override)
  trackColor,           // background ring color
  duration = 900,
  showPct = true,
  showFraction = false,
  testid = 'score-ring',
}) {
  const pctTarget = total > 0 ? Math.min(100, Math.max(0, (value / total) * 100)) : 0;
  const [pct, setPct] = useState(0);
  const rafRef = useRef(0);
  const startRef = useRef(null);
  const lastTargetRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setPct(pctTarget);
      return;
    }
    const from = lastTargetRef.current;
    const to = pctTarget;
    lastTargetRef.current = to;
    startRef.current = performance.now();

    const tick = (now) => {
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setPct(from + (to - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [pctTarget, duration]);

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct / 100);
  const ringColor = color || 'hsl(var(--primary))';
  const bgRing = trackColor || 'hsl(var(--border))';

  return (
    <div
      className="relative flex items-center justify-center c2r-score-ring"
      style={{ width: size, height: size }}
      data-testid={testid}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} stroke={bgRing} strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={ringColor}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ filter: pct > 0 ? `drop-shadow(0 0 6px ${ringColor})` : 'none', transition: 'filter 200ms ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center font-black tabular-nums leading-none">
        {showPct && (
          <span style={{ color: ringColor, fontSize: size * 0.26 }}>{Math.round(pct)}%</span>
        )}
        {showFraction && (
          <span className="text-muted-foreground mt-0.5" style={{ fontSize: size * 0.16 }}>
            {value}/{total}
          </span>
        )}
      </div>
    </div>
  );
}
